import { useState, useEffect, useCallback } from "react";
import { useDataProvider, useNotify, useRefresh } from "ra-core";
import type { ImportBatchRollbackInfo } from "./importEngine";

export function useImportRollback() {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [lastImport, setLastImport] = useState<ImportBatchRollbackInfo | null>(
    null,
  );
  const [isUndoing, setIsUndoing] = useState(false);

  const loadLastImport = useCallback(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        const raw = window.localStorage.getItem("dotus_last_import_batch");
        if (raw) {
          const parsed = JSON.parse(raw) as ImportBatchRollbackInfo;
          if (
            parsed &&
            ((parsed.dealIds && parsed.dealIds.length > 0) ||
              (parsed.contactIds && parsed.contactIds.length > 0) ||
              (parsed.companyIds && parsed.companyIds.length > 0))
          ) {
            setLastImport(parsed);
            return;
          }
        }
      } catch {
        // ignore JSON parse error
      }
      setLastImport(null);
    }
  }, []);

  useEffect(() => {
    loadLastImport();
    const handleUpdate = () => loadLastImport();
    window.addEventListener("dotus_import_batch_updated", handleUpdate);
    window.addEventListener("storage", handleUpdate);
    return () => {
      window.removeEventListener("dotus_import_batch_updated", handleUpdate);
      window.removeEventListener("storage", handleUpdate);
    };
  }, [loadLastImport]);

  const undoLastImport = useCallback(
    async (specificBatch?: ImportBatchRollbackInfo) => {
      const batch = specificBatch || lastImport;
      if (!batch) return false;

      setIsUndoing(true);
      try {
        let deletedDeals = 0;
        let deletedContacts = 0;
        let deletedCompanies = 0;

        // 1. Deletar Negócios criados nesta importação
        if (batch.dealIds && batch.dealIds.length > 0) {
          try {
            await dataProvider.deleteMany("deals", { ids: batch.dealIds });
            deletedDeals = batch.dealIds.length;
          } catch (err: any) {
            console.error("Erro ao excluir negócios importados:", err);
          }
        }

        // 2. Deletar Contatos criados nesta importação
        if (batch.contactIds && batch.contactIds.length > 0) {
          try {
            await dataProvider.deleteMany("contacts", {
              ids: batch.contactIds,
            });
            deletedContacts = batch.contactIds.length;
          } catch (err: any) {
            console.error("Erro ao excluir contatos importados:", err);
          }
        }

        // 3. Deletar Empresas criadas nesta importação
        if (batch.companyIds && batch.companyIds.length > 0) {
          try {
            await dataProvider.deleteMany("companies", {
              ids: batch.companyIds,
            });
            deletedCompanies = batch.companyIds.length;
          } catch (err: any) {
            console.error("Erro ao excluir empresas importadas:", err);
          }
        }

        // Limpa o lote no localStorage
        if (typeof window !== "undefined" && window.localStorage) {
          window.localStorage.removeItem("dotus_last_import_batch");
          window.dispatchEvent(new Event("dotus_import_batch_updated"));
        }
        setLastImport(null);

        const parts: string[] = [];
        if (deletedDeals > 0) parts.push(`${deletedDeals} oportunidade(s)`);
        if (deletedContacts > 0) parts.push(`${deletedContacts} contato(s)`);
        if (deletedCompanies > 0)
          parts.push(`${deletedCompanies} organização(ões)`);

        const summary = parts.length > 0 ? parts.join(", ") : "registros";
        notify(`Importação desfeita com sucesso! ${summary} removido(s).`, {
          type: "success",
        });
        refresh();
        return true;
      } catch (err: any) {
        notify(
          `Erro ao desfazer importação: ${err?.message || "Falha na exclusão"}`,
          { type: "error" },
        );
        return false;
      } finally {
        setIsUndoing(false);
      }
    },
    [lastImport, dataProvider, notify, refresh],
  );

  return {
    lastImport,
    hasLastImport: Boolean(
      lastImport &&
        ((lastImport.dealIds && lastImport.dealIds.length > 0) ||
          (lastImport.contactIds && lastImport.contactIds.length > 0) ||
          (lastImport.companyIds && lastImport.companyIds.length > 0)),
    ),
    isUndoing,
    undoLastImport,
    loadLastImport,
  };
}
